import ConfirmationDot from '@/components/ui/verification/confirmation-dot.tsx';
import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import { pdkAxios } from '@/lib/pdk-axios';
import type { PrimaryCustomer } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import InlineTextInput from '../../ui/inline-text-input';

const PrimaryCustomerEmailEditor = observer(() => {
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const email = deal?.primary_customer?.email || '';
  const customerId = deal?.primary_customer?.id || '';
  const dealId = deal?.id || '';
  const dealShortId = deal?.short_id || '';

  const [emailValue, setEmailValue] = useState(email);

  const queryClient = useQueryClient();
  const updatePrimaryCustomerEmailMutation = useMutation({
    onMutate: async ({ primaryCustomerEmail }) => {
      dealStore.updatePrimaryCustomerEmail(customerId, primaryCustomerEmail);
    },
    mutationFn: async ({
      primaryCustomerEmail,
    }: {
      primaryCustomerEmail: PrimaryCustomer['email'];
    }) => {
      if (!primaryCustomerEmail) return;

      const response = await pdkAxios
        .post(`/deals/${dealId}/primary-customer/${customerId}/email`, {
          email: primaryCustomerEmail,
        })
        .catch((e) => {
          console.error('Error updating primary customer email', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating primary customer email', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const handlePrimaryCustomerEmailChange = useCallback(
    debounce((newPrimaryCustomerEmail: string) => {
      updatePrimaryCustomerEmailMutation.mutate({
        primaryCustomerEmail: newPrimaryCustomerEmail,
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (emailValue === email) return;

    handlePrimaryCustomerEmailChange(emailValue);
  }, [emailValue]);

  useEffect(() => {
    setEmailValue(email);
  }, [email]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailValue(e.target.value);
  }, []);

  return (
    <div className="flex flex-row">
      <InlineTextInput
        key={`primary-customer-email-editor-${dealId}`}
        placeholder="Customer Email"
        value={emailValue}
        size="sm"
        className="w-full!"
        wrapperClassName="w-full!"
        onChange={onChange}
      />
      <ConfirmationDot
        targetId={dealId}
        targetType={ConfirmationTargetType.Deal}
        field={ConfirmationField.CustomerEmail}
        value={observable.box(emailValue)}
      />
    </div>
  );
});

export default PrimaryCustomerEmailEditor;
